/**
 * Business date — Asia/Tbilisi (D-169). THE single implementation, all layers.
 *
 * The trap this closes (already documented on the test layer in
 * tests/helpers/server-dates.ts, but production kept the UTC day): the business runs on
 * Asia/Tbilisi (UTC+4), so `new Date().toISOString()` names YESTERDAY's business day for four
 * hours of every evening (20:00–24:00 UTC = 00:00–04:00 Tbilisi) — and the Product Owner has
 * confirmed work happens around the clock, so that window is daily reality, not an edge case.
 *
 * One implementation, three consumers — never copy the conversion (the D-157 lesson: two
 * "identical" rules silently diverge):
 *   - web app: apps/web/src/lib/dates.ts re-exports these helpers; the display layer
 *     (apps/web/src/ui/data/format.ts) renders through the same `tbilisiParts`.
 *   - reporting services: validateAsOfDate (src/reporting/util/keyset.ts) and every service's
 *     metadata default — the gate and the UI must agree on "today" at every instant, otherwise
 *     the UI's default as-of is rejected as "future" during the evening window.
 *   - SQL: `app.business_today()` (migration 0103) is the same rule in Postgres; the
 *     cross-layer equality is asserted by tests, and both sides resolve the SAME IANA zone
 *     ('Asia/Tbilisi'), so a given instant can never map to two different business days.
 *
 * The conversion itself is `Intl.DateTimeFormat.formatToParts` with an explicit IANA timezone —
 * host-timezone-independent by construction, no offset arithmetic ever hand-rolled (Tbilisi is
 * UTC+4 with no DST today, but that is the tzdata's fact to know, not this module's).
 */
export const BUSINESS_TIME_ZONE = 'Asia/Tbilisi';

export interface TbilisiParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// `en-US` is used only to force Latin digits out of `formatToParts` — callers reassemble the
// numeric parts by hand, so the locale never influences any visible layout.
export function tbilisiParts(ms: number): TbilisiParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

/** The current business day (Asia/Tbilisi) as an ISO `YYYY-MM-DD` string. */
export function businessToday(): string {
  const p = tbilisiParts(Date.now());
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/**
 * The business day `days` days before today: the CURRENT Tbilisi day is resolved first, then the
 * offset is pure calendar arithmetic on its parts (`Date.UTC` on explicit numbers is
 * deterministic — the anchor is a plain calendar date, so UTC math cannot shift its day). The
 * predecessor mixed the two clocks — subtracted on the host-local day, then read the UTC day —
 * so `isoDaysAgo(0) !== isoToday()` for part of every day; this version guarantees
 * `businessDaysAgo(0) === businessToday()` at every instant.
 */
export function businessDaysAgo(days: number): string {
  const p = tbilisiParts(Date.now());
  return new Date(Date.UTC(p.year, p.month - 1, p.day - days)).toISOString().slice(0, 10);
}
